import React, {useContext, useState} from 'react';
import {Handle, Position, NodeResizer} from '@xyflow/react';
import {LineageContext} from './LineageContext';
import {Public as PublicIcon} from '@material-ui/icons';

/**
 * Visual-container group node (behavior 3/6). Resizable via NodeResizer (free RF
 * API); carries node-level handles so groups are connectable to nodes and other
 * groups (behavior 6). A caret in the label bar collapses the group to a single
 * node: members are hidden and their edges re-anchor onto this node (display-only,
 * computed in the editor via computeCollapse — `data.collapsed` is the only state).
 */
const GroupNode = ({id, data, selected}) => {
  const {viewMode, theme, actions} = useContext(LineageContext);
  const editable = viewMode === 'edit';
  const collapsed = !!data.collapsed;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEdit = (e) => {
    if (!editable) return;
    e.stopPropagation();
    setDraft(data.label || 'Group');
    setEditing(true);
  };
  const commit = () => {
    if (actions && actions.updateNode) actions.updateNode(id, {data: {label: draft.trim()}});
    setEditing(false);
  };
  const rootClass = [
    'ln-groupnode',
    data.__dbGroup ? 'ln-derived-in' : '', // a database's expansion container fades in (it can't flip)
    collapsed ? 'ln-collapsed' : '',
    selected ? 'ln-selected' : '',
    data.highlighted ? 'ln-highlighted' : '',
    data.dimmed ? 'ln-dimmed' : '',
  ].filter(Boolean).join(' ');

  const toggle = (e) => {
    e.stopPropagation();
    if (actions && actions.toggleGroupCollapse) actions.toggleGroupCollapse(id);
  };

  return (
    <div
      className={rootClass}
      // Always fill the RF node wrapper. The width/height props can be a stale measured
      // size (RF keeps applying the true size to the wrapper), so trusting them would
      // overflow the compact box when the group is collapsed.
      style={{'width': '100%', 'height': '100%', 'borderColor': theme.group, '--ln-group-color': theme.group}}
      data-testid={`groupnode-${id}`}
    >
      <NodeResizer
        color={theme.group}
        isVisible={selected && editable && !collapsed}
        minWidth={120}
        minHeight={90}
      />
      <Handle id="node-target" type="target" position={Position.Left} className="ln-handle ln-handle-node" style={{background: theme.group}} />
      <Handle id="node-source" type="source" position={Position.Right} className="ln-handle ln-handle-node" style={{background: theme.group}} />
      <div className="ln-group-label" style={{color: theme.group}}>
        <button
          type="button"
          className="ln-group-caret"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={toggle}
          title={collapsed ? 'Expand group' : 'Collapse group'}
          data-testid={`group-collapse-${id}`}
        >
          <i className="material-icons">{collapsed ? 'chevron_right' : 'expand_more'}</i>
        </button>
        {editing ? (
          <input
            className="ln-group-name-input"
            value={draft}
            autoFocus
            data-testid={`group-label-input-${id}`}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commit();
              } else if (e.key === 'Escape') {
                setEditing(false);
              }
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={(e) => e.stopPropagation()}
          />
        ) : (
          <span
            className="ln-group-name" data-testid={`group-label-${id}`}
            title={editable ? 'Double-click to rename' : (data.label || 'Group')}
            onClick={(e) => editable && !collapsed && e.stopPropagation()}
            onDoubleClick={startEdit}
          >{data.label || 'Group'}</span>
        )}
        {collapsed && !editing ? <span className="ln-group-count">{data.__memberCount || 0} nodes</span> : null}
        {data.__external && <PublicIcon titleAccess="External" data-testid={`group-external-icon-${id}`} style={{fontSize: 13, color: theme.group, flexShrink: 0}} />}
      </div>
    </div>
  );
};

export default React.memo(GroupNode);
